import React, { useState, useEffect, useCallback } from 'react';
import { Check, Loader2, ArrowLeft, Crown, Gift } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PricingPage = ({ user, onBack, onSuccess }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState(null);

  const checkPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    setCheckingPayment(true);
    const maxAttempts = 5;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        // Update local user data
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        userData.plan = response.data.plan;
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        
        onSuccess(userData);
        return;
      }
      
      if (attempts < maxAttempts) {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (err) {
      console.error('Error checking payment:', err);
    } finally {
      if (attempts >= maxAttempts - 1) {
        setCheckingPayment(false);
      }
    }
  }, [onSuccess]);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing`);
        setPlans(response.data.plans);
      } catch (err) {
        console.error('Error fetching pricing:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPricing();
    // Check if returning from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [checkPaymentStatus]);

  const handleSelectPlan = async (planId) => {
    setProcessingPlan(planId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/checkout/create?plan_id=${planId}`,
        {},
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Error creating checkout:', err);
      alert(err.response?.data?.detail || 'Error al procesar el pago');
      setProcessingPlan(null);
    }
  };

  const handleRedeemPromo = async (e) => {
    e.preventDefault();
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    setPromoMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/promo/redeem`,
        { code: promoCode },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Update local user data
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      userData.plan = response.data.plan;
      userData.plan_expires = response.data.plan_expires;
      localStorage.setItem('user', JSON.stringify(userData));
      
      setPromoMessage({ type: 'success', text: response.data.message });
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        onSuccess(userData);
      }, 2000);
      
    } catch (err) {
      console.error('Error redeeming promo:', err);
      setPromoMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Error al canjear el código' 
      });
    } finally {
      setPromoLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-cyan-400" />
        <p className="text-white text-xl">Verificando pago...</p>
        <p className="text-gray-400">Por favor, espera un momento</p>
      </div>
    );
  }

  const isPremium = user?.plan && user.plan !== 'free';

  return (
    <div className="min-h-screen bg-[#0a0a0b] py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-12">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold">Planes y Precios</h1>
            <p className="text-gray-400">
              {isPremium 
                ? `Tu plan actual: ${user.plan === 'lifetime' ? 'Acceso de por Vida' : user.plan === 'yearly' ? 'Plan Anual' : 'Plan Mensual'}`
                : 'Elige el plan que mejor se adapte a ti'
              }
            </p>
          </div>
        </div>

        {/* Current Plan Banner (if premium) */}
        {isPremium && (
          <div className="mb-8 p-6 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border border-cyan-500/20 rounded-xl flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white">¡Gracias por ser premium!</h3>
              <p className="text-gray-400">Tienes acceso ilimitado a todas las funcionalidades</p>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = user?.plan === plan.id;
            
            return (
              <div
                key={plan.id}
                className={`p-6 rounded-xl border transition-all ${
                  plan.popular
                    ? 'bg-gradient-to-b from-cyan-500/10 to-transparent border-cyan-500 relative'
                    : 'bg-[#0d0d0f] border-gray-800 hover:border-gray-700'
                } ${isCurrentPlan ? 'ring-2 ring-cyan-500' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full text-xs font-semibold">
                    MÁS POPULAR
                  </div>
                )}
                {plan.savings && (
                  <div className="inline-block px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium mb-2">
                    {plan.savings}
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="inline-block px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-xs font-medium mb-2 ml-2">
                    TU PLAN ACTUAL
                  </div>
                )}
                <h3 className="text-xl font-semibold mb-1">{plan.name}</h3>
                {plan.description && (
                  <p className="text-gray-500 text-sm mb-4">{plan.description}</p>
                )}
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}€</span>
                  {plan.limitedOffer && <span className="text-cyan-400 text-xl">*</span>}
                  <span className="text-gray-400 ml-1">/{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => !isCurrentPlan && !isPremium && handleSelectPlan(plan.id)}
                  disabled={isCurrentPlan || isPremium || processingPlan === plan.id}
                  className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                    isCurrentPlan
                      ? 'bg-cyan-500/20 text-cyan-400 cursor-default'
                      : isPremium
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                      : plan.popular
                      ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400'
                      : 'bg-gray-800 hover:bg-gray-700'
                  } disabled:opacity-50`}
                >
                  {processingPlan === plan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isCurrentPlan ? (
                    'Plan Actual'
                  ) : isPremium ? (
                    'Ya eres Premium'
                  ) : (
                    'Seleccionar'
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Limited offer note */}
        <p className="text-center text-gray-500 text-sm mt-6">
          * Oferta por tiempo limitado
        </p>

        {/* Promo Code Section */}
        {!isPremium && (
          <div className="mt-12 max-w-md mx-auto">
            <div className="p-6 bg-[#0d0d0f] border border-gray-800 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                  <Gift className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">¿Tienes un código promocional?</h3>
                  <p className="text-sm text-gray-400">Introdúcelo para obtener acceso gratuito</p>
                </div>
              </div>
              
              {promoMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  promoMessage.type === 'success' 
                    ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                  {promoMessage.text}
                </div>
              )}
              
              <form onSubmit={handleRedeemPromo} className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Ej: CODIGO123"
                  className="flex-1 px-4 py-3 bg-[#0a0a0b] border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 transition-colors uppercase"
                  data-testid="promo-code-input"
                />
                <button
                  type="submit"
                  disabled={promoLoading || !promoCode.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-medium hover:from-purple-400 hover:to-pink-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="promo-redeem-btn"
                >
                  {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Canjear'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Free Plan Info */}
        {!isPremium && (
          <div className="mt-8 text-center">
            <p className="text-gray-400">
              Plan gratuito: hasta 20 operaciones.{' '}
              <button onClick={onBack} className="text-cyan-400 hover:underline">
                Continuar con plan gratuito
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
